// eslint-disable-next-line no-unused-vars
import { h, createRef } from 'preact';
import { useState } from 'preact/hooks';
import PropTypes from 'prop-types';

// Components
import Item from './Item';
import DateInput from './DateInput';

const { __ } = wp.i18n;

/**
 * 'amount'   => 0, // The amount of posts that this term applies to within the current query.
 * 'id'       => 0, // ID of the term. Used for 'key' attribute.
 * 'name'     => '', // The name of the term. From WP_Term. Example: "Azure DevOps".
 * 'slug'     => '', // The slug of the term. From WP_Term. Example: "azure-devops".
 * 'value'    => '', // The URL value from the item. Matches object value. (likely should be string/slug because "query" and "s" will be strings).
 * 'selected' => '', // True if the value is selected, false if not.
 * 'taxonomy' => '', // Slug for the taxonomy this term belongs to.} props
 */

/**
 * The main component.
 *
 * @param {Object} props Props
 * @return {Object} JSX
 */
const Date = ( props ) => {
	const { group, label, items, startDate, endDate } = props;
	const [selectedItem] = items.filter( ( item ) => item.selected );
	const isDisabled = !selectedItem || 'custom-range' !== selectedItem.value;
	const inputContainerRef = createRef( null );
	const buttonApply = createRef( null );

	/**
	 * Check if the input is valid.
	 * @param {Node} input The input the validate.
	 * @return {boolean}
	 */
	const validateInput = ( input ) => {
		const parent = input.closest( '.date-picker-wrapper' );

		if ( input.validity.patternMismatch ) {
			input.setCustomValidity( window.MSRSearch.i18n.validation.patternMismatch );
		} else {
			input.setCustomValidity( '' );
		}

		// Set based on validity
		if ( input.validity.valid ) {
			input.removeAttribute( 'aria-invalid' );
			input.classList.remove( 'is-invalid' );
			parent.classList.remove( 'is-invalid' );
		} else {
			input.setAttribute( 'aria-invalid', true );
			input.classList.add( 'is-invalid' );
			parent.classList.add( 'is-invalid' );
		}

		return input.validity.valid;
	};

	/**
	 * Validation for inputs.
	 * @return {boolean} True if valid, false if not.
	 */
	const validateAllInputs = () => {
		const inputs = Array.from( inputContainerRef.current.querySelectorAll( '.duet-date__input' ) );
		const inputsValid = [];
		const inputsEmpty = [];

		inputs.forEach( ( input ) => {
			const isValid = validateInput( input );
			inputsValid.push( isValid );
			inputsEmpty.push( input.value.trim() );
		} );

		// No invalid inputs, no empty inputs
		return ! inputsValid.includes( false ) && !( '' === inputsEmpty[0] && '' === inputsEmpty[1] );
	};

	/**
	 * Set the "apply" button's enabled state.
	 * @param {string|Event} [state] Optional. If a string, sets the button state directly. 
	 * If an event object or undefined, determines state based on input validity.
	 */
	const setApplyButtonEnabledState = (state) => {
		let isEnabled;

		if (typeof state === 'string') {
			isEnabled = state === 'enabled';
		} else {
			// Enable the button if all date inputs are valid.
			isEnabled = validateAllInputs();
		}

		buttonApply.current.disabled = !isEnabled;
	};

	/**
	 * Handle on change.
	 *
	 * @param {object} event The event object.
	 */
	const handleOnChange = ( event ) => {
		const { target } = event;
		const fieldset = inputContainerRef.current.closest( 'fieldset' );
		const item = fieldset.querySelector( 'input[type="radio"]:checked' );
		const isCustomRangeChecked = 'custom-range' === item.value;
		const inputs = Array.from( inputContainerRef.current.querySelectorAll( 'duet-date-picker' ) );

		if (!isCustomRangeChecked) {
			setApplyButtonEnabledState('enabled');
		} else {
			setApplyButtonEnabledState();
		}

		// Radio fields
		if ( target.matches( 'input[type="radio"]' ) ) {

			// When it isn't a custom range...
			if (!isCustomRangeChecked) {
				// Disabled the inputs.
				inputs.forEach( ( input ) => {
					input.setAttribute( 'disabled', true );
					input.value = '';
				} );
			} else {
				// Don't bubble. Prevents auto submit.
				event.stopPropagation();

				// Enable buttons.
				inputs.forEach( ( input ) => {
					input.removeAttribute( 'disabled' );
				} );
			}
		}

		if ( target.matches( '.duet-date__input' ) ) {
			// Don't bubble. Prevents auto submit.
			event.stopPropagation();

			// Sometimes the duet input doesn't set the hidden one properly.
			// Make sure it gets set.
			target.nextElementSibling.value = target.value;

			setApplyButtonEnabledState();

			if ( target.id.includes( 'from' ) && validateInput( target ) ) {
				const fromInput = target.closest( 'duet-date-picker' );
				const [endInput] = inputs.filter( ( input ) => input !== fromInput );

				endInput.setAttribute( 'min', target.value );
			}

			return;
		}

		// Don't bubble. Prevents auto submit.
		event.stopPropagation();
	};

	/**
	 * Radio components.
	 * @returns
	 */
	const Radios = () => {
		return items.map( ( item ) => {
			const selected = item.selected || ( !selectedItem && 'date-range-any' === item.id );

			return (
				<Item
					item={{ ...item, selected }}
					key={item.id}
					group={group}
					type="radio"
					hidden={0 === parseInt( item.count ) && 'date-range-any' !== item.id && 'date-range-custom-range' !== item.id}
					hideCount={'date-range-custom-range' === item.id || 0 === parseInt( item.count )}
				/>
			);
		} );
	};

	// Render
	return (
		<fieldset className="form-group m-0" onChange={handleOnChange}>
			<legend className="h6 pb-2">{label}</legend>
			<div className="facet facet--date">
				<ul className="facet facet--list pl-0">
					<Radios />
				</ul>
				<div className="facet__container facet__container--range" ref={inputContainerRef}>
					<DateInput
						className="date-picker-wrapper"
						description={__( 'Enter a starting date in year (4 digits) - month (2 digits) - day (2 digits) format.', 'ms-research' )}
						name={__( 'from:', 'ms-research' )}
						slug="facet[date][range][from]"
						group={group}
						disabled={isDisabled}
						value={startDate}
						update={setApplyButtonEnabledState}
					/>
					<DateInput
						className="date-picker-wrapper"
						description={__( 'Enter a ending date in year (4 digits) - month (2 digits) - day (2 digits) format.', 'ms-research' )}
						name={__( 'to:', 'ms-research' )}
						slug="facet[date][range][to]"
						group={group}
						disabled={isDisabled}
						value={endDate}
						update={setApplyButtonEnabledState}
					/>
				</div>
				<button className="btn btn-apply btn-primary mt-3" data-bi-bhvr="4" ref={buttonApply}>
					{__( 'Apply date filters', 'ms-research' )}
				</button>
			</div>
		</fieldset>
	);
};

Date.propTypes = {
	endDate: PropTypes.string.isRequired,
	group: PropTypes.string.isRequired,
	items: PropTypes.array.isRequired,
	label: PropTypes.string.isRequired,
	startDate: PropTypes.string.isRequired,
};

export default Date;
